import React, { createContext, useContext, useReducer, useEffect } from 'react';

interface FavoriteProduct {
  id: string;
  name: string;
  price: number;
  image: string;
  category: string;
  rating: number;
  reviews: number;
  isNew?: boolean;
  isFeatured?: boolean;
  stock: number;
}

interface FavoritesState {
  favorites: FavoriteProduct[];
  itemCount: number;
}

type FavoritesAction =
  | { type: 'ADD_FAVORITE'; payload: FavoriteProduct }
  | { type: 'REMOVE_FAVORITE'; payload: string }
  | { type: 'CLEAR_FAVORITES' }
  | { type: 'LOAD_FAVORITES'; payload: FavoriteProduct[] };

const initialState: FavoritesState = {
  favorites: [],
  itemCount: 0,
};

const favoritesReducer = (state: FavoritesState, action: FavoritesAction): FavoritesState => {
  switch (action.type) {
    case 'ADD_FAVORITE':
      if (!state.favorites.find(fav => fav.id === action.payload.id)) {
        return {
          ...state,
          favorites: [...state.favorites, action.payload],
          itemCount: state.itemCount + 1,
        };
      }
      return state;
    
    case 'REMOVE_FAVORITE':
      return {
        ...state,
        favorites: state.favorites.filter(fav => fav.id !== action.payload),
        itemCount: state.itemCount - 1,
      };
    
    case 'CLEAR_FAVORITES':
      return {
        ...state,
        favorites: [],
        itemCount: 0,
      };
    
    case 'LOAD_FAVORITES':
      return {
        ...state,
        favorites: action.payload,
        itemCount: action.payload.length,
      };
    
    default:
      return state;
  }
};

interface FavoritesContextType {
  state: FavoritesState;
  addFavorite: (product: FavoriteProduct) => void;
  removeFavorite: (productId: string) => void;
  clearFavorites: () => void;
  isFavorite: (productId: string) => boolean;
  toggleFavorite: (product: FavoriteProduct) => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(favoritesReducer, initialState);

  // Load favorites from localStorage on mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem('totalbat-favorites');
    if (savedFavorites) {
      try {
        const favorites = JSON.parse(savedFavorites);
        dispatch({ type: 'LOAD_FAVORITES', payload: favorites });
      } catch (error) {
        console.error('Error loading favorites from localStorage:', error);
      }
    }
  }, []);

  // Save favorites to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('totalbat-favorites', JSON.stringify(state.favorites));
  }, [state.favorites]);

  const addFavorite = (product: FavoriteProduct) => {
    dispatch({ type: 'ADD_FAVORITE', payload: product });
  };

  const removeFavorite = (productId: string) => {
    dispatch({ type: 'REMOVE_FAVORITE', payload: productId });
  };

  const clearFavorites = () => {
    dispatch({ type: 'CLEAR_FAVORITES' });
  };

  const isFavorite = (productId: string): boolean => {
    return state.favorites.some(fav => fav.id === productId);
  };

  const toggleFavorite = (product: FavoriteProduct) => {
    if (isFavorite(product.id)) {
      removeFavorite(product.id);
    } else {
      addFavorite(product);
    }
  };

  const value: FavoritesContextType = {
    state,
    addFavorite,
    removeFavorite,
    clearFavorites,
    isFavorite,
    toggleFavorite,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = (): FavoritesContextType => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}; 